import {
  FlowVersion,
  NavigateDirective,
  flowStructureUtil,
  isNil,
} from '@activepieces/shared';
import { useEffect } from 'react';

import { useBuilderStateContext } from '../builder-hooks';

import { hasFlowStructureChanged } from './ai-builder-utils';
import { useChatStore } from './useChatStore';

import { sampleDataApi } from '@/features/flows/lib/sample-data-api';
import { sentry } from '@/lib/sentry';

/**
 * The single owner that applies a turn's answer to the canvas — mounted once
 * on the builder PAGE (not the chat sidebar, which mounts and unmounts as the
 * user opens and closes it). The turn's onSuccess only records the answer in
 * the store; this hook watches it and applies it exactly once, whether the
 * chat is open, closed, or the user left the flow and came back.
 */
export const useApplyAiAnswers = (flowId: string): void => {
  const {
    readonly,
    applyAiFlowUpdate,
    applyAiStepState,
    triggerAutoLayout,
    selectStepByName,
    setHighlightKey,
    setConnectionDialogOpen,
    setSampleData,
  } = useBuilderStateContext((state) => ({
    readonly: state.readonly,
    applyAiFlowUpdate: state.applyAiFlowUpdate,
    applyAiStepState: state.applyAiStepState,
    triggerAutoLayout: state.triggerAutoLayout,
    selectStepByName: state.selectStepByName,
    setHighlightKey: state.setHighlightKey,
    setConnectionDialogOpen: state.setConnectionDialogOpen,
    setSampleData: state.setSampleData,
  }));
  const pendingAnswer = useChatStore((state) => state.answersToApply[flowId]);
  const setAwaitingConnection = useChatStore(
    (state) => state.setAwaitingConnection,
  );

  // The builder loads sample data once at page mount; a turn that server-tested
  // steps (trigger sampling, auto-advance) changed it behind the panel's back.
  // Only steps carrying a sample file are fetched — never clearing local state.
  const refreshSampleData = async (version: FlowVersion) => {
    const testedSteps = flowStructureUtil.getAllSteps(version).filter(
      (step) =>
        !isNil(
          (
            step.settings as {
              inputUiInfo?: { sampleDataFileId?: string };
            }
          ).inputUiInfo?.sampleDataFileId,
        ),
    );
    await Promise.all(
      testedSteps.map(async (step) => {
        try {
          const payload = await sampleDataApi.get({
            flowId: version.flowId,
            flowVersionId: version.id,
            stepName: step.name,
          });
          setSampleData(step.name, payload);
        } catch (error) {
          // Degraded mode, visible: the panel stays stale until reload — not
          // worth failing the turn over, but silently losing the refresh
          // would make a stale panel indistinguishable from a working one
          sentry.captureException(
            error,
            'AI Builder sample data refresh failed',
            {
              tags: { feature: 'ai-builder', operation: 'refreshSampleData' },
              extra: { flowId: version.flowId, stepName: step.name },
            },
          );
        }
      }),
    );
  };

  const applyServerVersion = (
    serverVersion: FlowVersion,
    baselineVersion: FlowVersion | null,
  ) => {
    // Only take the server version when the turn actually changed the flow.
    // On a configure turn it is identical (or staler than) what the user is
    // editing in the panel: applying it resets the form, and the panel's
    // autosave would then persist those reset values.
    const structureChanged =
      !baselineVersion ||
      hasFlowStructureChanged(baselineVersion, serverVersion);
    if (structureChanged) {
      applyAiFlowUpdate(serverVersion);
      triggerAutoLayout();
    } else {
      applyAiStepState(serverVersion, baselineVersion);
    }
    // Even a structure-preserving turn may have tested steps server-side
    void refreshSampleData(serverVersion);
  };

  const followDirectives = (directives: NavigateDirective[]) => {
    // Any fresh answer supersedes a previous connect-account gate
    setAwaitingConnection(flowId, false);
    const lastDirective = directives[directives.length - 1];
    if (!lastDirective) return;
    if (lastDirective.stepName) {
      selectStepByName(lastDirective.stepName, 'assistant');
    }
    if (lastDirective.propToHighlight) {
      setHighlightKey(lastDirective.propToHighlight);
    }
    if (lastDirective.action === 'OPEN_AUTH') {
      setAwaitingConnection(flowId, true);
      setConnectionDialogOpen(true);
    }
  };

  useEffect(() => {
    // A read-only mount (a run's detail page, the internal-admin execution
    // viewer) shows an EXECUTED version, never the draft a turn would apply.
    // Leave the answer pending: the editable builder for this flow — if one
    // is ever opened — is the one that owns applying it.
    if (readonly) return;
    if (!pendingAnswer) return;
    const answer = useChatStore.getState().takeAnswerToApply(flowId);
    if (!answer) return;
    if (answer.newFlowVersion) {
      applyServerVersion(
        answer.newFlowVersion,
        answer.previousFlowVersion ?? null,
      );
    }
    followDirectives(answer.navigateDirectives ?? []);
    // applyServerVersion/followDirectives close over the builder-state setters
    // (stable Zustand actions) and flowId/pendingAnswer/readonly are the only
    // values that should re-trigger this effect — same pattern as the chat's
    // own effects in use-ai-builder-turn.ts.
  }, [pendingAnswer, flowId, readonly]);
};
